import { useState, useRef, useCallback, useEffect } from 'react';

type RecordingStatus = 'idle' | 'requesting_permission' | 'recording' | 'error';

// Silence detection constants
const VOLUME_THRESHOLD = 30; // out of 255 (getByteFrequencyData range)
const SILENCE_DURATION = 1500; // ms of silence after speech to trigger callback
const POLL_INTERVAL = 100; // ms between volume checks
const MIN_SPEECH_DURATION = 500; // ms — require some speech before watching for silence

export function useAudioRecording() {
  const [status, setStatus] = useState<RecordingStatus>('idle');
  const [volume, setVolume] = useState(0); // 0-1 normalized volume level
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const getSupportedMimeType = (): string | undefined => {
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
    for (const mime of candidates) {
      if (MediaRecorder.isTypeSupported(mime)) {
        return mime;
      }
    }
    return undefined;
  };

  const cleanupAudioContext = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
  }, []);

  const startRecording = useCallback(async (
    onSilenceDetected?: () => void
  ): Promise<boolean> => {
    // Check if MediaRecorder is available
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('error');
      return false;
    }

    setStatus('requesting_permission');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Component unmounted while awaiting permission — release stream immediately
      if (!mountedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return false;
      }

      streamRef.current = stream;

      const mimeType = getSupportedMimeType();
      if (!mimeType) {
        // No supported mime type — release stream and bail
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        setStatus('error');
        return false;
      }

      chunksRef.current = [];
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.start();
      setStatus('recording');

      // Set up AudioContext for volume monitoring + optional silence detection
      try {
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        analyserRef.current = analyser;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        let speechStartTime: number | null = null;
        let silenceStartTime: number | null = null;

        pollIntervalRef.current = setInterval(() => {
          if (!analyserRef.current) return;

          analyserRef.current.getByteFrequencyData(dataArray);
          // Average volume across frequency bins
          const avgVolume = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length;

          // Update normalized volume (0-1) for UI visualization
          setVolume(Math.min(avgVolume / 80, 1));

          // Silence detection (only if callback provided)
          if (onSilenceDetected) {
            if (avgVolume > VOLUME_THRESHOLD) {
              // Speech detected
              if (!speechStartTime) {
                speechStartTime = Date.now();
              }
              silenceStartTime = null;
            } else if (speechStartTime) {
              // Silence after speech
              const speechDuration = Date.now() - speechStartTime;
              if (speechDuration >= MIN_SPEECH_DURATION) {
                if (!silenceStartTime) {
                  silenceStartTime = Date.now();
                } else if (Date.now() - silenceStartTime >= SILENCE_DURATION) {
                  // Sustained silence after sufficient speech — trigger callback
                  cleanupAudioContext();
                  setVolume(0);
                  onSilenceDetected();
                }
              }
            }
          }
        }, POLL_INTERVAL);
      } catch {
        // AudioContext not supported — volume monitoring won't work
      }

      return true;
    } catch {
      // Permission denied or other error — release stream if acquired
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      setStatus('error');
      return false;
    }
  }, [cleanupAudioContext]);

  const stopRecording = useCallback((): Promise<Blob | null> => {
    cleanupAudioContext();

    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        resolve(null);
        setStatus('idle');
        return;
      }

      recorder.onstop = () => {
        const mimeType = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];
        mediaRecorderRef.current = null;

        // Release microphone tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }

        setStatus('idle');
        resolve(blob);
      };

      recorder.stop();
    });
  }, [cleanupAudioContext]);

  // Cleanup on unmount — release microphone if still recording
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanupAudioContext();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [cleanupAudioContext]);

  return { startRecording, stopRecording, status, volume };
}
